import type { AddressLookupGroup } from "../../../../../../components/Questionnaire/Section/Question/types";
import type { VisibilityMap } from "../../../../../../components/Questionnaire/validation/form/types";
import type { Answers } from "../../../../types";
import type { FlattenGroupResult } from "../types";
import {
  createAddressLookupQuestion,
  createEmptyFlattenResult,
} from "../utils";

/**
 * Handles address lookup group; includes a shadow controller question
 * for lookup & manual entry toggle and the address field questions.
 */
export const flattenAddressLookupGroup = (
  group: AddressLookupGroup,
  answers: Answers,
  visibility: VisibilityMap,
): FlattenGroupResult => {
  const { groupQuestions } = group;

  // If there are no address field questions, return empty result
  if (!groupQuestions?.length) {
    return createEmptyFlattenResult(answers, visibility);
  }

  // Build a shadow / controller question for lookup & manual entry toggle
  const addressLookupControllerQuestion = createAddressLookupQuestion(group);

  // Group visibility is derived from child questions
  const isGroupVisible = groupQuestions.some(q => q.visible);

  // Start with existing visibility map
  const visibilityByQuestionName: VisibilityMap = {
    ...visibility,
    [addressLookupControllerQuestion.name]: isGroupVisible,
  };
  const answersByQuestionName: Answers = { ...answers };

  const addressFieldQuestions = groupQuestions.map(q => {
    const hiddenOnAutocomplete = !!q.hiddenOnAutocomplete;
    /**
     * Answer checking priority for address fields:
     * 1. Question's own answer (from API/initial state) - q.answer
     * 2. Current form answers state - answersByQuestionName[q.name]
     *
     * Used to determine if field has a value (for visibility calculation).
     * Only stores q.answer if it exists; doesn't set defaults.
     */
    const hasAnswer = !!(q.answer || answersByQuestionName[q.name]);
    // Visible only if base visible, NOT hiddenOnAutocomplete, and has a (truthy) answer
    const shouldBeVisible = !!q.visible && !hiddenOnAutocomplete && hasAnswer;
    visibilityByQuestionName[q.name] = shouldBeVisible;
    if (q.answer) {
      answersByQuestionName[q.name] = q.answer;
    }
    // Set includeAnswer to true so these answers are sent to the BE even when hidden
    return { ...q, includeAnswer: true };
  });

  return {
    questions: [addressLookupControllerQuestion, ...addressFieldQuestions],
    groups: {
      [addressLookupControllerQuestion.name]: {
        kind: "address",
        names: addressFieldQuestions.map(q => q.name),
      },
    },
    visibility: visibilityByQuestionName,
    answers: answersByQuestionName,
  };
};
